import { Logger } from '_common/services';
import { EditorSelectionUtils, JsonRange } from 'Editor/services/_Common/Selection';
import { ELEMENTS } from 'Editor/services/consts';
import { NodeUtils } from 'Editor/services/DataManager';
import {
  BaseViewBuilder,
  BaseViewElement,
} from 'Editor/services/VisualizerManager/Views/ViewBuilders';

const DOCUMENT_STYLES: Editor.Edition.DocumentStyles[] = [
  'fontFamily',
  'fontSize',
  'color',
  'highlightColor',
  'paragraphColor',
  'bold',
  'italic',
  'underline',
  'alignment',
  'lineHeight',
  'lineRule',
  'spaceBefore',
  'spaceAfter',
  'leftIndentation',
  'rightIndentation',
  'specialIndent',
  'specialIndentValue',
  'vanish',
  'listId',
  'listLevel',
  'asb',
  'asa',
  'wc',
  'kn',
  'kl',
  'cts',
  'pbb',
  'otl',
  'tabs',
];

const OTHER_BLOCK_STYLES: Editor.Edition.OtherBlockStyles[] = ['s'] as const;

const BLOCK_STYLES: Editor.Edition.BlockStyles[] = [
  'bold',
  'italic',
  'fontFamily',
  'fontSize',
  'color',
  'underline',
  'strikethrough',
  'superscript',
  'subscript',
  'vanish',
  'paragraphColor',
  'highlightColor',
  'spaceBefore',
  'spaceAfter',
  'alignment',
  'lineHeight',
  'lineRule',
  'leftIndentation',
  'rightIndentation',
  'specialIndent',
  'specialIndentValue',
  'listId',
  'listLevel',
  'asb',
  'asa',
  'wc',
  'kn',
  'kl',
  'cts',
  'pbb',
  'otl',
  'tabs',
] as const;

const INLINE_STYLES: Editor.Edition.InlineStyles[] = [
  'bold',
  'italic',
  'fontFamily',
  'fontSize',
  'color',
  'highlightColor',
  'underline',
  'strikethrough',
  'superscript',
  'subscript',
  'vanish',
] as const;

const INLINE_BOOLEAN_STYLES: Editor.Edition.InlineStyles[] = ['bold', 'italic'] as const;
const INLINE_SINGLE_STATE_STYLES: Editor.Edition.InlineStyles[] = [
  'strikethrough',
  'superscript',
  'subscript',
  'underline',
  'vanish',
] as const;
const INLINE_EXCLUSIVE_STYLES: {
  [index in Editor.Edition.InlineStyles]?: Editor.Edition.InlineStyles;
} = {
  subscript: 'superscript',
  superscript: 'subscript',
} as const;

const ALLOWED_OTHER_STYLES_BY_BLOCK: Editor.Edition.OtherBlockStylesList = {
  p: ['s'],
} as const;

const ALLOWED_STYLES_BY_BLOCK: Editor.Edition.BlockStylesList = {
  p: [
    'bold',
    'italic',
    'fontFamily',
    'fontSize',
    'color',
    'underline',
    'strikethrough',
    'superscript',
    'subscript',
    'vanish',
    'paragraphColor',
    'highlightColor',
    'spaceBefore',
    'spaceAfter',
    'alignment',
    'lineHeight',
    'lineRule',
    'leftIndentation',
    'rightIndentation',
    'specialIndent',
    'specialIndentValue',
    'listId',
    'listLevel',
    'asb',
    'asa',
    'wc',
    'kn',
    'kl',
    'cts',
    'pbb',
    'otl',
    'tabs',
  ],
  tbl: ['vanish'],
  figure: ['vanish'],
  toc: ['vanish'],
  tol: ['vanish'],
  tof: ['vanish'],
  tot: ['vanish'],
  k: ['vanish'],
  a: ['vanish'],
} as const;

const ALLOWED_STYLES_INLINE: Editor.Edition.InlineStylesList = {
  p: [
    'bold',
    'italic',
    'fontFamily',
    'fontSize',
    'color',
    'highlightColor',
    'underline',
    'strikethrough',
    'superscript',
    'subscript',
    'vanish',
  ],
} as const;

const INDENTATION_LIMITS: Editor.Edition.IndentationLimits = {
  INDENTATION_STEP: 36,
  LEFT: {
    MANIPULATION_MIN: -1440,
    MANIPULATION_MAX: 1440,
    RENDER_MIN: -72,
    RENDER_MAX: 216,
  },
  RIGHT: {
    MANIPULATION_MIN: -1440,
    MANIPULATION_MAX: 1440,
    RENDER_MIN: -72,
    RENDER_MAX: 216,
  },
  SPECIAL_INDENT: {
    MANIPULATION_MIN: 0,
    MANIPULATION_MAX: 1440,
    RENDER_MIN: 0,
    RENDER_MAX: 216,
  },
} as const;

export class StylesUtils {
  private editionContext: Editor.Edition.Context;
  private stylesContext: Editor.Edition.Styles.Context;

  constructor(
    editionContext: Editor.Edition.Context,
    stylesContext: Editor.Edition.Styles.Context,
  ) {
    this.editionContext = editionContext;
    this.stylesContext = stylesContext;
  }

  static get INDENTATION_LIMITS() {
    return INDENTATION_LIMITS;
  }

  static get STYLES(): Editor.Edition.Styles[] {
    return [...BLOCK_STYLES, ...OTHER_BLOCK_STYLES, 'highlightColor'];
  }

  static get STYLABLE_BLOCKS() {
    return Object.typedKeys(ALLOWED_STYLES_BY_BLOCK);
  }

  static get INLINE_EXCLUSIVE_STYLES() {
    return INLINE_EXCLUSIVE_STYLES;
  }

  static get INLINE_BOOLEAN_STYLES() {
    return INLINE_BOOLEAN_STYLES;
  }

  static get BLOCK_STYLES(): Editor.Edition.BlockStyles[] {
    return BLOCK_STYLES;
  }

  static get INLINE_STYLES() {
    return INLINE_STYLES;
  }

  // #####################################
  // #region Styles Validations
  // #####################################
  static isDocumentStyle(style: any): style is Editor.Edition.DocumentStyles {
    return DOCUMENT_STYLES.includes(style);
  }

  static isOtherBlockStyle(style: any): style is Editor.Edition.OtherBlockStyles {
    return OTHER_BLOCK_STYLES.includes(style);
  }

  static isAllowedOtherBlockStyle(
    type: Editor.Data.Node.DataTypes,
    style: string,
  ): style is Editor.Edition.OtherBlockStyles {
    return (
      NodeUtils.isBlockDataType(type) &&
      StylesUtils.isOtherBlockStyle(style) &&
      !!ALLOWED_OTHER_STYLES_BY_BLOCK[type]?.includes(style)
    );
  }

  static isBlockStyle(style: any): style is Editor.Edition.BlockStyles {
    return BLOCK_STYLES.includes(style);
  }

  static isAllowedBlockStyle(
    type: Editor.Data.Node.DataTypes,
    style: string,
  ): style is Editor.Edition.BlockStyles {
    return (
      NodeUtils.isBlockDataType(type) &&
      StylesUtils.isBlockStyle(style) &&
      !!ALLOWED_STYLES_BY_BLOCK[type]?.includes(style)
    );
  }

  static getAllowedBlockStyles(type: Editor.Data.Node.DataTypes) {
    if (NodeUtils.isBlockDataType(type) && ALLOWED_STYLES_BY_BLOCK[type]) {
      return ALLOWED_STYLES_BY_BLOCK[type];
    }
    return [];
  }

  static isInlineStyle(style: any): style is Editor.Edition.InlineStyles {
    return INLINE_STYLES.includes(style);
  }

  static isAllowedInlineStyle(
    type: Editor.Data.Node.DataTypes,
    style: string,
  ): style is Editor.Edition.InlineStyles {
    return (
      NodeUtils.isBlockDataType(type) &&
      StylesUtils.isInlineStyle(style) &&
      !!ALLOWED_STYLES_INLINE[type]?.includes(style)
    );
  }

  static isInlineBooleanStyle(style: string): style is Editor.Edition.InlineBooleanStyles {
    return StylesUtils.isInlineStyle(style) && INLINE_BOOLEAN_STYLES.includes(style);
  }

  static isInlineExlusiveStyle(style: string): style is Editor.Edition.InlineExclusiveStyles {
    return StylesUtils.isInlineStyle(style) && !!INLINE_EXCLUSIVE_STYLES[style];
  }

  static isInlineSingleStateStyle(style: string): style is Editor.Edition.InlineStyles {
    return StylesUtils.isInlineStyle(style) && INLINE_SINGLE_STATE_STYLES.includes(style);
  }
  // #endregion Validations

  // #####################################
  // #region Styles Parsers
  // #####################################

  readonly OtherBlockStylesParser: Editor.Edition.OtherBlockStylesParser = {
    s: (blockData: Editor.Data.Node.Data) => {
      if (NodeUtils.isParagraphData(blockData)) {
        return blockData.properties.s;
      }

      return undefined;
    },
  };

  readonly BlockStylesParser: Editor.Edition.BlockStylesParser = {
    fontFamily: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInheritedStyles = true, includeInlineStyleFromBlock = false } = options;
      if (NodeUtils.isParagraphData(blockData)) {
        if (includeInlineStyleFromBlock && blockData.properties.ff != null) {
          return blockData.properties.ff;
        } else if (includeInheritedStyles && blockData.properties.s != null) {
          const docStyle = this.editionContext.DataManager?.styles.getDocumentStyleFromId(
            blockData.properties.s,
          );
          if (docStyle?.extendedP?.fontfamily != null) {
            return docStyle.extendedP.fontfamily;
          }
        }
      }

      return undefined;
    },
    fontSize: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInheritedStyles = true, includeInlineStyleFromBlock = false } = options;
      if (NodeUtils.isParagraphData(blockData)) {
        if (includeInlineStyleFromBlock && blockData.properties.fs != null) {
          return blockData.properties.fs;
        } else if (includeInheritedStyles && blockData.properties.s != null) {
          const docStyle = this.editionContext.DataManager?.styles.getDocumentStyleFromId(
            blockData.properties.s,
          );
          if (docStyle?.extendedP?.fontsize != null) {
            return docStyle.extendedP.fontsize;
          }
        }
      }

      return undefined;
    },
    color: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInheritedStyles = true, includeInlineStyleFromBlock = false } = options;
      // TODO convert colors from data????
      if (NodeUtils.isParagraphData(blockData)) {
        if (includeInlineStyleFromBlock && blockData.properties.c != null) {
          return blockData.properties.c;
        } else if (includeInheritedStyles && blockData.properties.s != null) {
          const docStyle = this.editionContext.DataManager?.styles.getDocumentStyleFromId(
            blockData.properties.s,
          );
          if (docStyle?.extendedP?.color != null) {
            return docStyle.extendedP.color;
          }
        }
      }

      return undefined;
    },
    paragraphColor: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInheritedStyles = true } = options;
      if (NodeUtils.isParagraphData(blockData)) {
        if (blockData.properties.bg != null) {
          return blockData.properties.bg;
        } else if (includeInheritedStyles && blockData.properties.s != null) {
          const docStyle = this.editionContext.DataManager?.styles.getDocumentStyleFromId(
            blockData.properties.s,
          );
          if (docStyle?.extendedP?.bg != null) {
            return docStyle.extendedP.bg;
          }
        }
      }

      return undefined;
    },
    highlightColor: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInlineStyleFromBlock = false } = options;
      if (NodeUtils.isParagraphData(blockData)) {
        if (includeInlineStyleFromBlock && blockData.properties.hc != null) {
          return blockData.properties.hc;
        }
      }

      return undefined;
    },
    bold: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInheritedStyles = true, includeInlineStyleFromBlock = false } = options;
      if (NodeUtils.isParagraphData(blockData)) {
        if (includeInlineStyleFromBlock && blockData.properties.b != null) {
          return blockData.properties.b;
        } else if (includeInheritedStyles && blockData.properties.s != null) {
          const docStyle = this.editionContext.DataManager?.styles.getDocumentStyleFromId(
            blockData.properties.s,
          );
          if (docStyle?.extendedP?.bold != null) {
            return docStyle.extendedP.bold;
          }
        }
      }

      return undefined;
    },
    italic: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInheritedStyles = true, includeInlineStyleFromBlock = false } = options;
      if (NodeUtils.isParagraphData(blockData)) {
        if (includeInlineStyleFromBlock && blockData.properties.i != null) {
          return blockData.properties.i;
        } else if (includeInheritedStyles && blockData.properties.s != null) {
          const docStyle = this.editionContext.DataManager?.styles.getDocumentStyleFromId(
            blockData.properties.s,
          );
          if (docStyle?.extendedP?.italic != null) {
            return docStyle.extendedP.italic;
          }
        }
      }

      return undefined;
    },
    underline: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInheritedStyles = true, includeInlineStyleFromBlock = false } = options;
      if (NodeUtils.isParagraphData(blockData)) {
        if (includeInlineStyleFromBlock && blockData.properties.u != null) {
          return blockData.properties.u;
        } else if (includeInheritedStyles && blockData.properties.s != null) {
          const docStyle = this.editionContext.DataManager?.styles.getDocumentStyleFromId(
            blockData.properties.s,
          );
          if (docStyle?.extendedP?.underline != null) {
            return docStyle.extendedP.underline;
          }
        }
      }

      return undefined;
    },
    strikethrough: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInlineStyleFromBlock = false } = options;
      if (NodeUtils.isParagraphData(blockData)) {
        if (includeInlineStyleFromBlock && blockData.properties.strikethrough != null) {
          return blockData.properties.strikethrough;
        }
      }

      return undefined;
    },
    superscript: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInlineStyleFromBlock = false } = options;
      if (NodeUtils.isParagraphData(blockData)) {
        if (includeInlineStyleFromBlock && blockData.properties.sps != null) {
          return blockData.properties.sps;
        }
      }
      return undefined;
    },
    subscript: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInlineStyleFromBlock = false } = options;
      if (NodeUtils.isParagraphData(blockData)) {
        if (includeInlineStyleFromBlock && blockData.properties.sbs != null) {
          return blockData.properties.sbs;
        }
      }
      return undefined;
    },
    alignment: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInheritedStyles = true } = options;
      if (NodeUtils.isParagraphData(blockData)) {
        if (blockData.properties.a != null) {
          return blockData.properties.a;
        } else if (includeInheritedStyles && blockData.properties.s != null) {
          const docStyle = this.editionContext.DataManager?.styles.getDocumentStyleFromId(
            blockData.properties.s,
          );
          if (docStyle?.extendedP?.a != null) {
            return docStyle.extendedP.a;
          }
        }
      }

      return undefined;
    },
    lineHeight: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInheritedStyles = true } = options;
      if (NodeUtils.isParagraphData(blockData)) {
        if (blockData.properties.lh != null) {
          return blockData.properties.lh;
        } else if (includeInheritedStyles && blockData.properties.s != null) {
          const docStyle = this.editionContext.DataManager?.styles.getDocumentStyleFromId(
            blockData.properties.s,
          );
          if (docStyle?.extendedP?.lh != null) {
            return docStyle.extendedP.lh;
          }
        }
      }

      return undefined;
    },
    lineRule: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInheritedStyles = true } = options;
      if (NodeUtils.isParagraphData(blockData)) {
        if (blockData.properties.lr != null) {
          return blockData.properties.lr;
        } else if (includeInheritedStyles && blockData.properties.s != null) {
          const docStyle = this.editionContext.DataManager?.styles.getDocumentStyleFromId(
            blockData.properties.s,
          );
          if (docStyle?.extendedP?.lr != null) {
            return docStyle.extendedP.lr;
          }
        }
      }

      return undefined;
    },
    spaceBefore: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInheritedStyles = true } = options;
      if (NodeUtils.isParagraphData(blockData)) {
        if (blockData.properties.sb != null) {
          return blockData.properties.sb;
        } else if (includeInheritedStyles && blockData.properties.s != null) {
          const docStyle = this.editionContext.DataManager?.styles.getDocumentStyleFromId(
            blockData.properties.s,
          );
          if (docStyle?.extendedP?.sb != null) {
            return docStyle.extendedP.sb;
          }
        }
      }

      return undefined;
    },
    spaceAfter: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInheritedStyles = true } = options;
      if (NodeUtils.isParagraphData(blockData)) {
        if (blockData.properties.sa != null) {
          return blockData.properties.sa;
        } else if (includeInheritedStyles && blockData.properties.s != null) {
          const docStyle = this.editionContext.DataManager?.styles.getDocumentStyleFromId(
            blockData.properties.s,
          );
          if (docStyle?.extendedP?.sa != null) {
            return docStyle.extendedP.sa;
          }
        }
      }

      return undefined;
    },
    leftIndentation: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInheritedStyles = true } = options;
      if (blockData.id && NodeUtils.isParagraphData(blockData)) {
        // get list defenition if exist
        let listLevelDefenition;
        if (this.editionContext.DataManager?.numbering?.isListElement(blockData.id)) {
          listLevelDefenition =
            this.editionContext.DataManager?.numbering?.getLevelDefinitionFromBlock(blockData.id);
        }

        if (blockData.properties.ind?.l != null) {
          return blockData.properties.ind.l;
        } else if (listLevelDefenition && listLevelDefenition.ind?.l) {
          return listLevelDefenition.ind.l;
        } else if (includeInheritedStyles && blockData.properties.s != null) {
          const docStyle = this.editionContext.DataManager?.styles.getDocumentStyleFromId(
            blockData.properties.s,
          );
          if (docStyle?.extendedP?.ind?.l != null) {
            return docStyle.extendedP.ind.l;
          }
        }
      }

      return undefined;
    },
    rightIndentation: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInheritedStyles = true } = options;
      if (NodeUtils.isParagraphData(blockData)) {
        if (blockData.properties.ind?.r != null) {
          return blockData.properties.ind.r;
        } else if (includeInheritedStyles && blockData.properties.s != null) {
          const docStyle = this.editionContext.DataManager?.styles.getDocumentStyleFromId(
            blockData.properties.s,
          );
          if (docStyle?.extendedP?.ind?.r != null) {
            return docStyle.extendedP.ind.r;
          }
        }
      }

      return undefined;
    },
    specialIndent: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInheritedStyles = true } = options;
      if (blockData.id && NodeUtils.isParagraphData(blockData)) {
        // get list defenition if exist
        let listLevelDefenition;
        if (this.editionContext.DataManager?.numbering?.isListElement(blockData.id)) {
          listLevelDefenition =
            this.editionContext.DataManager?.numbering?.getLevelDefinitionFromBlock(blockData.id);
        }

        if (blockData.properties.sp_ind?.t != null) {
          return blockData.properties.sp_ind?.t;
        } else if (
          listLevelDefenition &&
          listLevelDefenition.sp_ind &&
          listLevelDefenition.sp_ind.t
        ) {
          return listLevelDefenition.sp_ind.t as Editor.Data.Node.SpecialIndentation['t'];
        } else if (includeInheritedStyles && blockData.properties.s != null) {
          const docStyle = this.editionContext.DataManager?.styles.getDocumentStyleFromId(
            blockData.properties.s,
          );
          if (docStyle?.extendedP?.sp_ind?.t != null) {
            return docStyle.extendedP.sp_ind.t;
          }
        }
      }

      return undefined;
    },
    specialIndentValue: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInheritedStyles = true } = options;
      if (blockData.id && NodeUtils.isParagraphData(blockData)) {
        // get list defenition if exist
        let listLevelDefenition;
        if (this.editionContext.DataManager?.numbering?.isListElement(blockData.id)) {
          listLevelDefenition =
            this.editionContext.DataManager?.numbering?.getLevelDefinitionFromBlock(blockData.id);
        }
        if (blockData.properties.sp_ind?.v != null) {
          return blockData.properties.sp_ind?.v;
        } else if (
          listLevelDefenition &&
          listLevelDefenition.sp_ind &&
          listLevelDefenition.sp_ind.v
        ) {
          return listLevelDefenition.sp_ind.v;
        } else if (includeInheritedStyles && blockData.properties.s != null) {
          const docStyle = this.editionContext.DataManager?.styles.getDocumentStyleFromId(
            blockData.properties.s,
          );
          if (docStyle?.extendedP?.sp_ind?.v != null) {
            return docStyle.extendedP.sp_ind.v;
          }
        }
      }

      return undefined;
    },
    vanish: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInheritedStyles = true } = options;
      if (blockData.properties?.v != null) {
        return blockData.properties.v;
      } else if (includeInheritedStyles && blockData.properties?.s != null) {
        const docStyle = this.editionContext.DataManager?.styles.getDocumentStyleFromId(
          blockData.properties.s,
        );
        if (docStyle?.extendedP?.v != null) {
          return docStyle.extendedP.v;
        }
      }

      return undefined;
    },
    listId: (blockData: Editor.Data.Node.Data, options = {}) => {
      if (blockData.id && this.editionContext.DataManager?.numbering.isListElement(blockData.id)) {
        return this.editionContext.DataManager?.numbering.getListIdFromBlock(blockData.id);
      } else if (NodeUtils.isParagraphData(blockData) && blockData.id) {
        // WARN if the block is not a list,  we should check if document style has list properties and force the list to be null
        const docStyle = this.editionContext.DataManager?.styles.getDocumentStyleFromId(
          blockData.properties?.s,
        );
        if (docStyle?.extendedP?.lst?.lId != null) {
          return null;
        }
      }
      return undefined;
    },
    listLevel: (blockData: Editor.Data.Node.Data, options = {}) => {
      if (blockData.id && this.editionContext.DataManager?.numbering.isListElement(blockData.id)) {
        return this.editionContext.DataManager?.numbering.getListLevelFromBlock(blockData.id);
      } else if (NodeUtils.isParagraphData(blockData) && blockData.id) {
        // WARN if the block is not a list,  we should check if document style has list properties and force the list to be null
        const docStyle = this.editionContext.DataManager?.styles.getDocumentStyleFromId(
          blockData.properties?.s,
        );
        if (docStyle?.extendedP?.lst?.lLv != null) {
          return null;
        }
      }
      return undefined;
    },
    asb: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInheritedStyles = true } = options;
      if (NodeUtils.isParagraphData(blockData)) {
        if (blockData.properties.asb != null) {
          return blockData.properties.asb;
        } else if (includeInheritedStyles && blockData.properties.s != null) {
          const docStyle = this.editionContext.DataManager?.styles.getDocumentStyleFromId(
            blockData.properties.s,
          );
          if (docStyle?.extendedP?.asb != null) {
            return docStyle.extendedP.asb;
          }
        }
      }

      return undefined;
    },
    asa: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInheritedStyles = true } = options;
      if (NodeUtils.isParagraphData(blockData)) {
        if (blockData.properties.asa != null) {
          return blockData.properties.asa;
        } else if (includeInheritedStyles && blockData.properties.s != null) {
          const docStyle = this.editionContext.DataManager?.styles.getDocumentStyleFromId(
            blockData.properties.s,
          );
          if (docStyle?.extendedP?.asa != null) {
            return docStyle.extendedP.asa;
          }
        }
      }

      return undefined;
    },
    wc: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInheritedStyles = true } = options;
      if (NodeUtils.isParagraphData(blockData)) {
        if (blockData.properties.wc != null) {
          return blockData.properties.wc;
        } else if (includeInheritedStyles && blockData.properties.s != null) {
          const docStyle = this.editionContext.DataManager?.styles.getDocumentStyleFromId(
            blockData.properties.s,
          );
          if (docStyle?.extendedP?.wc != null) {
            return docStyle.extendedP.wc;
          }
        }
      }

      return undefined;
    },
    kn: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInheritedStyles = true } = options;
      if (NodeUtils.isParagraphData(blockData)) {
        if (blockData.properties.kn != null) {
          return blockData.properties.kn;
        } else if (includeInheritedStyles && blockData.properties.s != null) {
          const docStyle = this.editionContext.DataManager?.styles.getDocumentStyleFromId(
            blockData.properties.s,
          );
          if (docStyle?.extendedP?.kn != null) {
            return docStyle.extendedP.kn;
          }
        }
      }

      return undefined;
    },
    kl: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInheritedStyles = true } = options;
      if (NodeUtils.isParagraphData(blockData)) {
        if (blockData.properties.kl != null) {
          return blockData.properties.kl;
        } else if (includeInheritedStyles && blockData.properties.s != null) {
          const docStyle = this.editionContext.DataManager?.styles.getDocumentStyleFromId(
            blockData.properties.s,
          );
          if (docStyle?.extendedP?.kl != null) {
            return docStyle.extendedP.kl;
          }
        }
      }

      return undefined;
    },
    cts: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInheritedStyles = true } = options;
      if (NodeUtils.isParagraphData(blockData)) {
        if (blockData.properties.cts != null) {
          return blockData.properties.cts;
        } else if (includeInheritedStyles && blockData.properties.s != null) {
          const docStyle = this.editionContext.DataManager?.styles.getDocumentStyleFromId(
            blockData.properties.s,
          );
          if (docStyle?.extendedP?.cts != null) {
            return docStyle.extendedP.cts;
          }
        }
      }

      return undefined;
    },
    pbb: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInheritedStyles = true } = options;
      if (NodeUtils.isParagraphData(blockData)) {
        if (blockData.properties.pbb != null) {
          return blockData.properties.pbb;
        } else if (includeInheritedStyles && blockData.properties.s != null) {
          const docStyle = this.editionContext.DataManager?.styles.getDocumentStyleFromId(
            blockData.properties.s,
          );
          if (docStyle?.extendedP?.pbb != null) {
            return docStyle.extendedP.pbb;
          }
        }
      }

      return undefined;
    },
    otl: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInheritedStyles = true } = options;
      if (NodeUtils.isParagraphData(blockData)) {
        if (blockData.properties.otl != null) {
          return blockData.properties.otl;
        } else if (includeInheritedStyles && blockData.properties.s != null) {
          const docStyle = this.editionContext.DataManager?.styles.getDocumentStyleFromId(
            blockData.properties.s,
          );
          if (docStyle?.extendedP?.otl != null) {
            return docStyle.extendedP.otl;
          }
        }
      }

      return undefined;
    },
    tabs: (blockData: Editor.Data.Node.Data, options = {}) => {
      const { includeInheritedStyles = true } = options;
      if (NodeUtils.isParagraphData(blockData)) {
        if (blockData.properties.tabs != null) {
          return blockData.properties.tabs;
        } else if (includeInheritedStyles && blockData.properties.s != null) {
          const docStyle = this.editionContext.DataManager?.styles.getDocumentStyleFromId(
            blockData.properties.s,
          );
          if (docStyle?.extendedP?.tabs != null) {
            return docStyle.extendedP.tabs;
          }
        }
      }

      return [];
    },
  };

  readonly InlineStylesParser: Editor.Edition.InlineStylesParser = {
    bold: (formatData: Editor.Data.Node.FormatData) =>
      formatData.properties.bold != null ? formatData.properties.bold : undefined,
    color: (formatData: Editor.Data.Node.FormatData) =>
      formatData.properties.color != null ? formatData.properties.color : undefined,
    italic: (formatData: Editor.Data.Node.FormatData) =>
      formatData.properties.italic != null ? formatData.properties.italic : undefined,
    underline: (formatData: Editor.Data.Node.FormatData) =>
      formatData.properties.underline != null ? formatData.properties.underline : undefined,
    strikethrough: (formatData: Editor.Data.Node.FormatData) =>
      formatData.properties.strikethrough != null ? formatData.properties.strikethrough : undefined,
    superscript: (formatData: Editor.Data.Node.FormatData) =>
      formatData.properties.superscript != null ? formatData.properties.superscript : undefined,
    subscript: (formatData: Editor.Data.Node.FormatData) =>
      formatData.properties.subscript != null ? formatData.properties.subscript : undefined,
    vanish: (formatData: Editor.Data.Node.FormatData) =>
      formatData.properties.v != null ? formatData.properties.v : undefined,
    highlightColor: (formatData: Editor.Data.Node.FormatData) =>
      formatData.properties.backgroundcolor != null
        ? formatData.properties.backgroundcolor
        : undefined,
    fontFamily: (formatData: Editor.Data.Node.FormatData) =>
      formatData.properties.fontfamily != null ? formatData.properties.fontfamily : undefined,
    fontSize: (formatData: Editor.Data.Node.FormatData) =>
      formatData.properties.fontsize != null ? formatData.properties.fontsize : undefined,
  };
  // #endregion Parsers

  // #####################################
  // #region Styles Utilities
  // #####################################
  private getOtherBlockStyles(
    baseData: Editor.Data.Node.Data,
    pathToElement: Editor.Selection.Path,
    stylesToCheck: Editor.Edition.Styles[],
  ) {
    let otherBlockStyles: Editor.Edition.StylesMap = {};

    let pathToCheck = [...pathToElement];
    let closesStylable = NodeUtils.closestOfTypeByPath(
      baseData,
      pathToCheck,
      StylesUtils.STYLABLE_BLOCKS,
    );

    while (closesStylable && NodeUtils.isBlockDataType(closesStylable.data.type)) {
      for (let i = 0; i < stylesToCheck.length; i++) {
        const styleProp = stylesToCheck[i];
        if (
          StylesUtils.isAllowedOtherBlockStyle(closesStylable.data.type, styleProp) &&
          otherBlockStyles[styleProp] == null
        ) {
          const styleValue = this.OtherBlockStylesParser[styleProp](closesStylable.data);

          if (styleValue != null) {
            otherBlockStyles[styleProp] = styleValue;
          }
        }
      }

      if (pathToCheck.length > 0) {
        pathToCheck = pathToCheck.slice(0, pathToCheck.length - 2);
        closesStylable = NodeUtils.closestOfTypeByPath(
          baseData,
          pathToCheck,
          StylesUtils.STYLABLE_BLOCKS,
        );
      } else {
        closesStylable = null;
      }
    }

    return otherBlockStyles;
  }

  private getBlockStyles(
    baseData: Editor.Data.Node.Data,
    pathToElement: Editor.Selection.Path,
    stylesToCheck: Editor.Edition.Styles[],
    options: Editor.Edition.Styles.GetStylesOptions = {},
  ): Editor.Edition.StylesMap {
    let pathToCheck = [...pathToElement];

    let blockStyles: Editor.Edition.StylesMap = {};

    let closesStylable = NodeUtils.closestOfTypeByPath(
      baseData,
      pathToCheck,
      StylesUtils.STYLABLE_BLOCKS,
    );

    while (closesStylable && NodeUtils.isBlockDataType(closesStylable.data.type)) {
      for (let i = 0; i < stylesToCheck.length; i++) {
        const styleProp = stylesToCheck[i];

        // if (
        //   !options.includeInlineStyleFromBlock &&
        //   StylesUtils.isAllowedInlineStyle(closesStylable.data.type, styleProp)
        // ) {
        //   continue;
        // }

        if (
          StylesUtils.isAllowedBlockStyle(closesStylable.data.type, styleProp) &&
          blockStyles[styleProp] == null
        ) {
          const styleValue = this.BlockStylesParser[styleProp](closesStylable.data, options);

          if (styleValue !== undefined) {
            //@ts-expect-error
            blockStyles[styleProp] = styleValue;
          }
        }
      }

      if (pathToCheck.length > 0) {
        pathToCheck = pathToCheck.slice(0, pathToCheck.length - 2);
        closesStylable = NodeUtils.closestOfTypeByPath(
          baseData,
          pathToCheck,
          StylesUtils.STYLABLE_BLOCKS,
        );
      } else {
        closesStylable = null;
      }
    }

    return blockStyles;
  }

  private getInlineStyles(
    baseData: Editor.Data.Node.Data,
    pathToElement: Editor.Selection.Path,
    stylesToCheck: Editor.Edition.Styles[],
  ): Editor.Edition.StylesMap {
    let pathToCheck = [...pathToElement];

    let inlineStyles: Editor.Edition.StylesMap = {};

    let elementsToSearch: Editor.Data.Node.DataTypes[] = ['format'];

    let closesStylable = NodeUtils.closestOfTypeByPath(baseData, pathToCheck, elementsToSearch);
    let closesStylableBlock = NodeUtils.closestOfTypeByPath(
      baseData,
      pathToCheck,
      StylesUtils.STYLABLE_BLOCKS,
    );

    while (
      closesStylableBlock &&
      closesStylable &&
      NodeUtils.isFormatData(closesStylable.data) &&
      pathToCheck.length > 0
    ) {
      for (let i = 0; i < stylesToCheck.length; i++) {
        const styleProp = stylesToCheck[i];

        if (
          StylesUtils.isAllowedInlineStyle(closesStylableBlock.data.type, styleProp) &&
          inlineStyles[styleProp] == null
        ) {
          const styleValue = this.InlineStylesParser[styleProp](closesStylable.data);

          if (styleValue != null) {
            //@ts-expect-error
            inlineStyles[styleProp] = styleValue;
          }
        }
      }

      pathToCheck = pathToCheck.slice(0, pathToCheck.length - 2);
      closesStylable = NodeUtils.closestOfTypeByPath(baseData, pathToCheck, elementsToSearch);
    }

    return inlineStyles;
  }

  private aggregateStyles(
    aggregatedMap: Editor.Edition.AggregatedStylesMap,
    stylesToAgregate: Editor.Edition.StylesMap,
  ) {
    const styleKeys = Object.typedKeys(stylesToAgregate);

    for (let i = 0; i < styleKeys.length; i++) {
      const style = styleKeys[i];

      if (aggregatedMap[style] == null) {
        aggregatedMap[style] = [];
      }

      const value = stylesToAgregate[style] as never;

      if (value !== undefined && !aggregatedMap[style].includes(value)) {
        aggregatedMap[style].push(value);
      }
    }

    return aggregatedMap;
  }

  getStylesAppliedToContent(
    baseData: Editor.Data.Node.Data,
    pathToElement: Editor.Selection.Path,
    stylesToCheck: Editor.Edition.Styles[],
    options: Editor.Edition.Styles.GetStylesOptions = {},
  ): Editor.Edition.StylesMap {
    // const elementToCheck = NodeUtils.getChildDataByPath(baseData, pathToElement);

    const inlineStyles = this.getInlineStyles(baseData, pathToElement, stylesToCheck);
    const blockStyles = this.getBlockStyles(baseData, pathToElement, stylesToCheck, options);

    let otherBlockStyles = {};
    if (options.includeOtherStyles) {
      otherBlockStyles = this.getOtherBlockStyles(baseData, pathToElement, stylesToCheck);
    }

    const mergedStyles = {
      ...blockStyles,
      ...inlineStyles,
      ...otherBlockStyles,
    };

    return mergedStyles;
  }

  getStylesAppliedToData(
    data: Editor.Data.Node.Data,
    stylesType: 'BLOCK' | 'INLINE',
    options: Editor.Edition.Styles.GetStylesOptions = {},
  ) {
    let stylesToCheck: Editor.Edition.Styles[] = [];

    if (stylesType === 'BLOCK') {
      stylesToCheck = [...StylesUtils.BLOCK_STYLES];
    } else {
      stylesToCheck = [...StylesUtils.INLINE_STYLES];
    }

    let stylesMap: Editor.Edition.StylesMap = {};

    if (NodeUtils.isBlockDataType(data.type) && StylesUtils.STYLABLE_BLOCKS.includes(data.type)) {
      for (let i = 0; i < stylesToCheck.length; i++) {
        const styleProp = stylesToCheck[i];

        if (stylesMap[styleProp] == null) {
          if (stylesType === 'BLOCK' && StylesUtils.isAllowedBlockStyle(data.type, styleProp)) {
            const styleValue = this.BlockStylesParser[styleProp](data, options);

            if (styleValue != null) {
              //@ts-expect-error
              stylesMap[styleProp] = styleValue;
            }
          } else if (
            stylesType === 'INLINE' &&
            StylesUtils.isAllowedInlineStyle(data.type, styleProp)
          ) {
            const styleValue = this.BlockStylesParser[styleProp](data, options);

            if (styleValue != null) {
              //@ts-expect-error
              stylesMap[styleProp] = styleValue;
            }
          }
        }
      }
    } else if (NodeUtils.isFormatData(data)) {
      for (let i = 0; i < stylesToCheck.length; i++) {
        const styleProp = stylesToCheck[i];

        if (StylesUtils.isInlineStyle(styleProp) && stylesMap[styleProp] == null) {
          const styleValue = this.InlineStylesParser[styleProp](data);

          if (styleValue != null) {
            //@ts-expect-error
            stylesMap[styleProp] = styleValue;
          }
        }
      }
    }

    return stylesMap;
  }

  getStylesAppliedToSelection(
    range: Editor.Selection.JsonRange,
    options: Editor.Edition.Styles.GetStylesOptions = {},
  ) {
    // get styles applied to:
    // selection inside paragraph
    // selection in multiple paragraphs
    // selection in multiple cells

    let stylesMap: Editor.Edition.AggregatedStylesMap = {};

    if (this.editionContext.DataManager) {
      const blockRangesData = JsonRange.splitRangeByTypes(
        this.editionContext.DataManager,
        range,
        NodeUtils.BLOCK_TEXT_TYPES,
        {
          sameContainerLevel: false,
          useSelectedCells: true,
        },
      );

      for (let i = 0; i < blockRangesData.length; i++) {
        const subRange = blockRangesData[i].range;

        const dataInfo = subRange.getNodes(this.editionContext.DataManager, ['text']);

        if (dataInfo.length === 0) {
          dataInfo.push({
            baseData: blockRangesData[i].baseData,
            childData: blockRangesData[i].filteredData,
            childPath: blockRangesData[i].filteredDataPath,
          });
        }

        for (let i = 0; i < dataInfo.length; i++) {
          const info = dataInfo[i];

          // avoid node if start selection is at the end of the content of that node and the range is not collapsed
          // ex: select a word, apply bold, start selection is at the end of content of the previous node, and the end selection is at end of content inside format element
          const subChildPath = subRange.start.p.slice(info.childPath.length);
          if (
            !subRange.isCollapsed() &&
            NodeUtils.isPathAtContentEnd(info.childData, subChildPath)
          ) {
            continue;
          }

          const styles = this.getStylesAppliedToContent(
            info.baseData,
            info.childPath,
            StylesUtils.STYLES,
            options,
          );

          stylesMap = this.aggregateStyles(stylesMap, styles);
        }
      }
    }

    if (this.stylesContext.debug) {
      Logger.debug('[StylesUtils] getStylesAppliedToSelection', stylesMap);
    }

    return stylesMap;
  }

  getStylesAppliedToNode(
    element: BaseViewElement,
    stylesToCheck: Editor.Edition.Styles[],
    options: Editor.Edition.Styles.GetStylesOptions = {},
  ) {
    let stylesMap: Editor.Edition.StylesMap = {};
    if (this.editionContext.DataManager) {
      const blockNode = element.getParentBlockElement();

      if (blockNode instanceof HTMLElement) {
        const blockModel = this.editionContext.DataManager.nodes.getNodeModelById(blockNode.id);
        const baseData = blockModel?.selectedData();

        if (baseData) {
          const childDataInfo = NodeUtils.getChildDataById(baseData, element.id);

          if (childDataInfo) {
            stylesMap = this.getStylesAppliedToContent(
              baseData,
              childDataInfo.path,
              stylesToCheck,
              options,
            );
          }
        }
      }
    }

    return stylesMap;
  }

  getNextIndentStep(current: number, next: boolean) {
    const INDENTATION_MIN = INDENTATION_LIMITS.LEFT.RENDER_MIN;
    const INDENTATION_MAX = INDENTATION_LIMITS.LEFT.RENDER_MAX;
    const INDENTATION_VALUE = INDENTATION_LIMITS.INDENTATION_STEP;

    const value = next ? current + INDENTATION_VALUE : current;
    const rest = value % INDENTATION_VALUE;

    let result = current;

    if (Number(parseFloat(rest.toString()).toFixed(2)) === 0) {
      result = next ? current + INDENTATION_VALUE : current - INDENTATION_VALUE;
    } else {
      result = next ? value : value - rest;
    }

    if (result > 0) {
      return Math.min(INDENTATION_MAX, result);
    }
    return Math.max(INDENTATION_MIN, result);
  }

  // #endregion Styles Utilities

  // #####################################
  // #region Image Utilities
  // #####################################
  getSelectedImageProperties(): Editor.Edition.ImageProperties | undefined {
    try {
      if (!this.editionContext.DataManager) {
        return undefined;
      }

      let properties: Editor.Edition.ImageProperties = {};

      const rangeData = this.editionContext.DataManager.selection.current;
      const jsonRange: Editor.Selection.JsonRange = JsonRange.buildFromRangeData(rangeData[0]);

      if (jsonRange) {
        const baseModel = this.editionContext.DataManager.nodes.getNodeModelById(jsonRange.start.b);

        const baseData = baseModel?.selectedData();

        if (baseData && baseData.id) {
          const imageDataInfo = NodeUtils.closestOfTypeByPath(
            baseData,
            jsonRange.getCommonAncestorPath(),
            ['image-element', 'img'],
          );
          const data = imageDataInfo?.data;

          if (NodeUtils.isImageData(data)) {
            const float = data.properties.f;
            const behindDoc = data.properties.bd;
            const wrapping = data.properties.wr;
            const offsetX = data.properties.ox;
            const alignX = data.properties.ax;

            let imageWrap: Editor.Edition.ImageWrapProperty = 'inline';

            if (float) {
              // non inline

              const pageWidth = this.editionContext.DataManager.sections.getPageWidthForBlockId(
                baseData.id,
              );

              switch (wrapping?.t) {
                case 'n': {
                  // position absolute
                  if (behindDoc) {
                    // behind text
                    imageWrap = 'behindText';
                  } else {
                    // in front of text
                    imageWrap = 'inFrontText';
                  }
                  break;
                }
                case 't':
                case 'ti':
                case 's': {
                  // wrap text / float
                  if (
                    (offsetX != null && pageWidth != null && offsetX > pageWidth / 2) ||
                    alignX === 'r'
                  ) {
                    // float right
                    imageWrap = 'right';
                  } else {
                    // float left
                    imageWrap = 'left';
                  }
                  break;
                }
                case 'tb': {
                  // top and bottom
                  imageWrap = 'topAndBottom';
                  break;
                }
              }
            }

            properties = {
              imageWrap: {
                value: imageWrap,
              },
              margins: {
                top: {
                  value: wrapping?.td || 0,
                },
                bottom: {
                  value: wrapping?.bd || 0,
                },
                left: {
                  value: wrapping?.ld || 0,
                },
                right: {
                  value: wrapping?.rd || 0,
                },
              },
            };
          }
        }
      }

      if (this.stylesContext.debug) {
        Logger.debug('[StylesUtils] getSelectedImageProperties', properties);
      }

      return properties;
    } catch (error) {
      Logger.warn(error);
    }
  }
  // #endregion Image Utilities

  // #####################################
  // #region Table Utilities
  // #####################################

  private getPropertiesFromCell = (
    tableData: Editor.Data.Node.TableData,
    cellData: Editor.Data.Node.TableCellData,
  ) => {
    const data: Editor.Edition.TableProperties = {};

    const background = BaseViewBuilder.colorMapper.render(
      cellData.properties?.bg || tableData.properties?.bg || 'false',
    );
    data.background = background;

    data.verticalAlignment = cellData.properties?.va || tableData.properties?.cva || 't';

    data.textAlignment = cellData.properties?.a || tableData.properties?.ca || 'l';

    let paddingData: Editor.Data.Node.Padding | undefined = undefined;
    if (cellData.properties?.p) {
      paddingData = cellData.properties.p;
    } else if (tableData.properties?.cp) {
      paddingData = tableData.properties.cp;
    }

    if (paddingData) {
      const padding: Editor.Edition.TableProperties['padding'] = {};

      if (paddingData.t) {
        padding.top = {
          value: +paddingData.t,
          incoherent: false,
        };
      }

      if (paddingData.b) {
        padding.bottom = {
          value: +paddingData.b,
          incoherent: false,
        };
      }

      if (paddingData.l) {
        padding.left = {
          value: +paddingData.l,
          incoherent: false,
        };
      }

      if (paddingData.r) {
        padding.right = {
          value: +paddingData.r,
          incoherent: false,
        };
      }

      data.padding = padding;
    }

    let borderData: Editor.Data.Node.Borders | undefined = undefined;
    if (cellData.properties?.b) {
      borderData = cellData.properties.b;
    } else if (tableData.properties?.cb) {
      borderData = tableData.properties.cb;
    }

    if (borderData) {
      const border: Editor.Edition.TableProperties['border'] = {};

      const color = borderData.t?.c || borderData.b?.c || borderData.r?.c || borderData.l?.c;
      border.color = {
        value: BaseViewBuilder.colorMapper.render(color) || '',
        incoherent: !(
          borderData.t?.c !== undefined &&
          borderData.t.c === borderData.r?.c &&
          borderData.t.c === borderData.b?.c &&
          borderData.t.c === borderData.l?.c
        ),
      };

      border.style = {
        value: borderData.t?.s || borderData.b?.s || borderData.r?.s || borderData.l?.s || 'n',
        incoherent: !(
          borderData.t?.s !== undefined &&
          borderData.t.s === borderData.r?.s &&
          borderData.t.s === borderData.b?.s &&
          borderData.t.s === borderData.l?.s
        ),
      };

      border.width = {
        value: borderData.t?.w || borderData.b?.w || borderData.r?.w || borderData.l?.w || 0,
        incoherent: !(
          borderData.t?.w !== undefined &&
          borderData.t.w === borderData.r?.w &&
          borderData.t.w === borderData.b?.w &&
          borderData.t.w === borderData.l?.w
        ),
      };

      data.border = border;
    }

    return data;
  };

  getSelectedTableProperties(): Editor.Edition.TablePropertiesData | null {
    try {
      if (!this.editionContext.DataManager || !this.editionContext.navigationManager) {
        return null;
      }

      this.editionContext.navigationManager.scrollIntoSelection();

      // get html selected elements
      const elements = EditorSelectionUtils.getSelectedTableElements();

      // No table was found
      if (!elements) {
        return null;
      }

      const { selectedTable, selectedRows, rowsIndex, columnsIndex, selectedCells } = elements;

      const rangeData = this.editionContext.DataManager.selection.current;
      const jsonRange: Editor.Selection.JsonRange = JsonRange.buildFromRangeData(rangeData[0]);

      if (jsonRange) {
        const baseModel = this.editionContext.DataManager.nodes.getNodeModelById(jsonRange.start.b);

        const baseData = baseModel?.selectedData();

        if (baseModel && baseData) {
          const selectedTableInfo = NodeUtils.closestOfTypeByPath(
            baseData,
            jsonRange.getCommonAncestorPath(),
            'tbl',
          );
          const tableData = selectedTableInfo?.data;

          let pageWidth = this.editionContext.DataManager?.sections.getPageWidthForBlockId(
            baseModel.id,
          );

          const data: Editor.Edition.TablePropertiesData = {
            PAGE: {
              width: pageWidth,
            },
            TABLE: {},
            ROW: {
              fromStart: false,
            },
            COLUMN: {},
            CELL: {},
          };

          if (NodeUtils.isTableData(tableData)) {
            const tbodyData: Editor.Data.Node.Data | undefined = tableData.childNodes?.find(
              (child) => child.type === ELEMENTS.TableElement.ELEMENTS.TABLE_BODY.ELEMENT_TYPE,
            );

            const rowsData = tbodyData?.childNodes;

            // Height is the sum of the rows' heights
            const rows = selectedTable?.tBodies[0].rows;

            if (tableData.properties?.w) {
              data.TABLE.width = {
                value: {
                  t: tableData.properties.w.t,
                  v: tableData.properties.w.v,
                },
                incoherent: false,
              };
            }

            if (tableData.properties) {
              const padding: Editor.Edition.PaddingProp = {};

              if (tableData.properties?.cp?.t) {
                padding.top = {
                  value: +tableData.properties.cp.t,
                  incoherent: false,
                };
              }

              if (tableData.properties?.cp?.b) {
                padding.bottom = {
                  value: +tableData.properties.cp.b,
                  incoherent: false,
                };
              }

              if (tableData.properties?.cp?.l) {
                padding.left = {
                  value: +tableData.properties.cp.l,
                  incoherent: false,
                };
              }

              if (tableData.properties?.cp?.r) {
                padding.right = {
                  value: +tableData.properties.cp.r,
                  incoherent: false,
                };
              }

              data.TABLE.padding = padding;

              data.TABLE.alignment = tableData.properties.a || 'l';
              data.TABLE.textAlignment = tableData.properties.ca || 'l';
              data.TABLE.leftIndentation = tableData.properties.ind
                ? tableData.properties.ind.l
                : 0;
              data.TABLE.verticalAlignment = tableData.properties.cva || 't';
              data.TABLE.background =
                BaseViewBuilder.colorMapper.render(tableData.properties.bg) || '';

              if (tableData.properties.cb) {
                const cb = tableData.properties.cb;
                const tableBorder: Editor.Edition.TableProperties['border'] = {};

                const color = cb.t?.c || cb.b?.c || cb.r?.c || cb.l?.c;
                tableBorder.color = {
                  value: BaseViewBuilder.colorMapper.render(color) || '',
                  incoherent: !(
                    cb.t?.c !== undefined &&
                    cb.t.c === cb.r?.c &&
                    cb.t.c === cb.b?.c &&
                    cb.t.c === cb.l?.c
                  ),
                };

                tableBorder.style = {
                  value: cb.t?.s || cb.b?.s || cb.r?.s || cb.l?.s || 'n',
                  incoherent: !(
                    cb.t?.s !== undefined &&
                    cb.t.s === cb.r?.s &&
                    cb.t.s === cb.b?.s &&
                    cb.t.s === cb.l?.s
                  ),
                };

                tableBorder.width = {
                  value: cb.t?.w || cb.b?.w || cb.r?.w || cb.l?.w || 0,
                  incoherent: !(
                    cb.t?.w !== undefined &&
                    cb.t.w === cb.r?.w &&
                    cb.t.w === cb.b?.w &&
                    cb.t.w === cb.l?.w
                  ),
                };
                data.TABLE.border = tableBorder;
              }

              data.TABLE.autoResize = tableData.properties.ar || false;
            }

            if (rows?.length && selectedRows[0].id === rows[0].id) {
              data.ROW.fromStart = true;
            }

            const columnsIds: string[] = [];
            if (rowsData) {
              let columnWidth:
                | Editor.Edition.IncoherentProperty<Editor.Data.Node.TableWidth>
                | undefined;
              let rowHeight:
                | Editor.Edition.IncoherentProperty<Editor.Data.Node.RowHeight>
                | undefined
                | null;
              let headerRows: string[] = [];

              for (let i = 0; i < rowsData.length; i++) {
                const rowData = rowsData[i];

                if (rowsIndex.includes(i)) {
                  if (rowData.properties?.hr === true && rowData.id) {
                    headerRows.push(rowData.id);
                  }

                  if (rowData.properties?.rh != null) {
                    if (rowHeight == null) {
                      if (isNaN(rowData.properties?.rh)) {
                        rowHeight = {
                          value: {
                            t: rowData.properties?.rh.t,
                            v: rowData.properties?.rh.v,
                          },
                          incoherent: false,
                        };
                      } else {
                        rowHeight = {
                          value: {
                            t: 'abs',
                            v: rowData.properties?.rh,
                          },
                          incoherent: false,
                        };
                      }
                    } else if (
                      rowHeight?.value.t !== rowData.properties.rh.t ||
                      rowHeight?.value.v !== rowData.properties.rh.v
                    ) {
                      rowHeight.incoherent = true;
                    }
                  } else {
                    if (rowHeight == null) {
                      rowHeight = {
                        value: {
                          t: 'auto',
                          v: 0,
                        },
                        incoherent: false,
                      };
                    } else {
                      rowHeight.incoherent = true;
                    }
                  }
                }

                if (rowData.childNodes) {
                  for (let j = 0; j < rowData.childNodes.length; j++) {
                    if (columnsIndex.includes(j)) {
                      const cellData = rowData?.childNodes?.[j];

                      if (cellData && cellData.properties?.w) {
                        if (columnWidth === undefined) {
                          columnWidth = {
                            value: { t: cellData.properties.w.t, v: cellData.properties.w.v },
                            incoherent: false,
                          };
                        } else if (
                          columnWidth !== null &&
                          columnWidth.value.t !== cellData.properties.w.t &&
                          columnWidth.value.v !== cellData.properties.w.v
                        ) {
                          if (
                            (columnWidth.value.t !== 'abs' && cellData.properties.w.t === 'abs') ||
                            (columnWidth.value.t === cellData.properties.w.t &&
                              columnWidth.value.v < cellData.properties.w.v)
                          ) {
                            columnWidth.value.t = cellData.properties.w.t;
                            columnWidth.value.v = cellData.properties.w.v;
                          }

                          columnWidth.incoherent = true;
                        }
                      } else {
                        if (columnWidth === undefined) {
                          columnWidth = {
                            value: { t: 'auto', v: 0 },
                            incoherent: false,
                          };
                        }
                      }

                      if (cellData?.id) {
                        columnsIds.push(cellData.id);
                      }
                    }
                  }
                }
              }
              if (headerRows.length === 0) {
                data.ROW.headerRow = {
                  value: false,
                  incoherent: false,
                };
              } else if (headerRows.length === selectedRows.length) {
                data.ROW.headerRow = {
                  value: true,
                  incoherent: false,
                };
              } else if (headerRows.length < selectedRows.length) {
                data.ROW.headerRow = {
                  value: false,
                  incoherent: true,
                };
              }

              if (rowHeight != null) {
                data.ROW.height = rowHeight;
                data.CELL.height = rowHeight;
              }

              if (columnWidth) {
                data.COLUMN.width = columnWidth;
                data.CELL.width = columnWidth;
              }
            }

            const cellData = baseModel.getChildDataById(selectedCells[0].id);

            if (NodeUtils.isTableCellData(cellData)) {
              const cellProperties = this.getPropertiesFromCell(tableData, cellData);

              data.ROW = { ...data.ROW, ...cellProperties };
              data.COLUMN = { ...data.COLUMN, ...cellProperties };
              data.CELL = { ...data.CELL, ...cellProperties };
            }
          }

          if (this.stylesContext.debug) {
            Logger.debug('[StylesUtils] getSelectedTableProperties', data);
          }

          return data;
        }
      }

      return null;
    } catch (error) {
      Logger.warn(error);
      return null;
    }
  }
  // #endregion Table Utilities
}
